// Portal Template Types

export type TemplateId = 'modern' | 'classic' | 'minimalist';

export type SectionId = 'news' | 'documents' | 'contact' | 'issues' | 'calendar' | 'gallery';

export interface PortalColors {
  primary: string;
  secondary: string;
  accent?: string;
  background?: string;
  text?: string;
}

export interface PortalSettings {
  id?: string;
  organization_id: string;
  template_id: TemplateId;
  colors: PortalColors;
  logo_url?: string;
  hero_image_url?: string;
  visible_sections: SectionId[];
  custom_domain?: string;
  meta_title?: string;
  meta_description?: string;
  footer_text?: string;
  hide_phone?: boolean;
  hide_email?: boolean;
  hide_address?: boolean;
  contact_address?: string;
  social_links?: {
    facebook?: string;
    instagram?: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface NewsItem {
  id: string;
  title: string;
  content: string;
  image_url?: string;
  published_at: string;
  author?: string;
}

export interface DocumentItem {
  id: string;
  name: string;
  category: string;
  file_url: string;
  file_size?: number;
  uploaded_at: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start_date: string;
  end_date?: string;
  location?: string;
}

export interface GalleryImage {
  id: string;
  url: string;
  thumbnail_url?: string;
  caption?: string;
  uploaded_at: string;
}

export interface ContactInfo {
  board_email?: string;
  board_phone?: string;
  address?: string;
  office_hours?: string;
  board_members?: Array<{
    name: string;
    role: string;
    email?: string;
    phone?: string;
  }>;
}

export interface PortalData {
  settings: PortalSettings;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  news: NewsItem[];
  documents: DocumentItem[];
  calendar: CalendarEvent[];
  gallery: GalleryImage[];
  contact: ContactInfo;
}

// Template configuration
export interface TemplateConfig {
  id: TemplateId;
  name: string;
  description: string;
  preview_image: string;
  default_colors: PortalColors;
}

export const TEMPLATE_CONFIGS: TemplateConfig[] = [
  {
    id: 'modern',
    name: 'Modern',
    description: 'Stora bilder, gradient-header, kortbaserad layout',
    preview_image: '/templates/modern-preview.png',
    default_colors: {
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#F59E0B',
      background: '#F8FAFC',
      text: '#1E293B'
    }
  },
  {
    id: 'classic',
    name: 'Klassisk',
    description: 'Traditionell layout med sidebar-navigation',
    preview_image: '/templates/classic-preview.png',
    default_colors: {
      primary: '#1E3A5F',
      secondary: '#2D5A87',
      accent: '#C9A227',
      background: '#FAFAFA',
      text: '#333333'
    }
  },
  {
    id: 'minimalist',
    name: 'Minimalistisk',
    description: 'Mycket whitespace, enkel typografi, clean design',
    preview_image: '/templates/minimalist-preview.png',
    default_colors: {
      primary: '#000000',
      secondary: '#666666',
      accent: '#0066CC',
      background: '#FFFFFF',
      text: '#1A1A1A'
    }
  }
];

export const SECTION_CONFIGS = [
  { id: 'news' as SectionId, name: 'Nyheter', icon: 'Newspaper', description: 'Aktuellt och nyheter från föreningen' },
  { id: 'documents' as SectionId, name: 'Dokument', icon: 'FileText', description: 'Stadgar, årsredovisningar och andra dokument' },
  { id: 'contact' as SectionId, name: 'Kontakt', icon: 'Phone', description: 'Kontaktinformation till styrelsen' },
  { id: 'issues' as SectionId, name: 'Felanmälan', icon: 'AlertTriangle', description: 'Formulär för att anmäla fel' },
  { id: 'calendar' as SectionId, name: 'Kalender', icon: 'Calendar', description: 'Kommande händelser och möten' },
  { id: 'gallery' as SectionId, name: 'Bildgalleri', icon: 'Image', description: 'Bilder från föreningen' }
];
